<template>
  <div class="min-h-screen flex flex-col bg-white">
    <!-- Custom CSS Injection for plain HTML snippets (injected into main document) -->
    <component :is="'style'" v-if="projectData?.mockupCSS && isPlainHTML" v-html="projectData.mockupCSS"></component>
    
    <!-- Main Preview Content Area - Full width, no constraints, with bottom padding for fixed bar -->
    <div class="flex-1 relative overflow-auto" :class="mockupContainerClass" :style="{ paddingBottom: bottomBarHeight + 'px' }">
      <!-- Loading State -->
      <div v-if="loading" class="min-h-screen flex items-center justify-center bg-[#111111]">
        <div class="text-center">
          <p class="text-[#EAEAEA] font-mono">Loading preview...</p>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error && !projectData" class="min-h-screen flex items-center justify-center bg-[#111111] p-6">
        <div class="max-w-2xl w-full">
          <div class="bg-[#121212] border border-[#2C2C2C] rounded-xl p-8 text-center">
            <h2 class="font-serif text-2xl text-[#EAEAEA] mb-4">Error Loading Preview</h2>
            <div class="p-4 bg-[#3a1d1d] border border-[#FF7F7F] rounded-lg">
              <p class="text-sm text-[#FF7F7F] font-mono">{{ error }}</p>
            </div>
            <p class="text-sm text-[#888] mt-4 font-mono">Project ID: {{ projectId }}</p>
          </div>
        </div>
      </div>

      <!-- Dynamic Preview Content - Using iframe for full HTML document support -->
      <iframe
        v-if="projectData?.mockupHTML && !isPlainHTML"
        ref="mockupIframe"
        class="mockup-iframe"
        frameborder="0"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
      ></iframe>
      
      <!-- Fallback to direct HTML injection for simple HTML snippets -->
      <div v-else-if="projectData?.mockupHTML && isPlainHTML" v-html="projectData.mockupHTML" class="mockup-content"></div>

      <!-- Fallback if no preview content -->
      <div v-else class="min-h-screen flex items-center justify-center bg-[#111111] p-6">
        <div class="max-w-2xl w-full">
          <div class="bg-[#121212] border border-[#2C2C2C] rounded-xl p-8 text-center">
            <h2 class="font-serif text-2xl text-[#EAEAEA] mb-4">No Preview Content</h2>
            <p class="text-base text-[#B0B0B0] mb-4">Preview content has not been added for this project yet.</p>
            <p class="text-sm text-[#888] font-mono">Project: {{ projectData?.projectName || projectId }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Bar - Fixed at bottom, always visible, compact for mobile -->
    <div ref="bottomBar" class="fixed bottom-0 left-0 right-0 z-50 bg-[#121212] border-t border-[#2C2C2C] px-3 sm:px-6 md:px-20 py-2 sm:py-3">
      <div class="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-4">
        <!-- Project Information -->
        <div class="flex flex-row items-center gap-3 sm:gap-6 md:gap-8 w-full sm:w-auto justify-between sm:justify-start">
          <div class="text-left flex-1 sm:flex-none">
            <p class="text-[9px] sm:text-[10px] text-[#555] uppercase tracking-widest font-mono mb-0.5 sm:mb-1">
              Project Name
            </p>
            <p class="text-xs sm:text-sm md:text-base text-[#EAEAEA] font-medium leading-tight sm:leading-normal">
              {{ projectData?.projectName || 'Loading...' }}
            </p>
          </div>
          <div class="hidden sm:block w-px h-6 md:h-8 bg-[#2C2C2C]"></div>
          <div class="text-left sm:text-left flex-1 sm:flex-none text-right sm:text-left">
            <p class="text-[9px] sm:text-[10px] text-[#555] uppercase tracking-widest font-mono mb-0.5 sm:mb-1">
              Date Updated
            </p>
            <p class="text-xs sm:text-sm md:text-base text-[#B0B0B0] font-mono leading-tight sm:leading-normal">
              {{ formattedDate }}
            </p>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex items-center gap-2 w-full sm:w-auto mt-2 sm:mt-0">
          <button
            @click="shareLink"
            class="flex items-center gap-1.5 bg-[#1a1a1a] border border-[#2C2C2C] text-[#EAEAEA] font-medium px-4 sm:px-5 py-1.5 sm:py-2 rounded-lg hover:border-[#B0B0B0] transition duration-300 uppercase tracking-wide text-[10px] sm:text-xs md:text-sm whitespace-nowrap flex-1 sm:flex-none justify-center"
          >
            <svg v-if="!copied" xmlns="http://www.w3.org/2000/svg" class="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0 text-green-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <span :class="{ 'text-green-400': copied }">{{ copied ? 'Copied!' : 'Share' }}</span>
          </button>
          <button
            @click="goToLogin"
            class="bg-white text-black font-medium px-4 sm:px-6 py-1.5 sm:py-2 rounded-lg hover:opacity-90 transition duration-300 uppercase tracking-wide text-[10px] sm:text-xs md:text-sm whitespace-nowrap flex-1 sm:flex-none"
          >
            Back To Login
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getFirestore, doc, getDoc } from 'firebase/firestore'
import { app } from '../firebase/config'

export default {
  name: 'MockupView',
  props: {
    projectId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      projectData: null,
      loading: true,
      error: null,
      iframeSetup: false,
      bottomBarHeight: 80,
      copied: false
    }
  },
  computed: {
    formattedDate() {
      if (!this.projectData?.dateUpdated) return 'N/A'
      
      const date = this.projectData.dateUpdated.toDate ? 
        this.projectData.dateUpdated.toDate() : 
        new Date(this.projectData.dateUpdated)
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    mockupContainerClass() {
      // Allow custom background class from Firestore, default to white
      if (this.projectData?.mockupBackgroundClass) {
        return this.projectData.mockupBackgroundClass
      }
      return 'bg-white'
    },
    isPlainHTML() {
      // Check if HTML is a full document (has <html>, <head>, or <body> tags) or just a snippet
      if (!this.projectData?.mockupHTML) return true
      const html = this.projectData.mockupHTML.toLowerCase()
      // If it contains document structure tags, treat as full document
      return !(html.includes('<!doctype') || html.includes('<html') || html.includes('<head') || html.includes('<body'))
    }
  },
  async mounted() {
    await this.loadProjectData()
    // Wait for next tick to ensure HTML is rendered
    await this.$nextTick()
    this.calculateBottomBarHeight()
    this.setupMockup()
    
    // Recalculate on window resize
    window.addEventListener('resize', this.calculateBottomBarHeight)
  },
  beforeUnmount() {
    // Clean up event listener
    window.removeEventListener('resize', this.calculateBottomBarHeight)
  },
  watch: {
    projectData: {
      handler(newVal, oldVal) {
        // Only setup if projectData actually changed and we have HTML
        if (newVal?.mockupHTML && newVal !== oldVal) {
          // Reset iframe setup flag when project data changes
          this.iframeSetup = false
          // Wait for next tick to ensure HTML is rendered
          this.$nextTick(() => {
            this.setupMockup()
          })
        }
      },
      deep: false // Don't watch deeply to avoid unnecessary triggers
    }
  },
  methods: {
    async loadProjectData() {
      try {
        console.log('Loading project data for ID:', this.projectId)
        const db = getFirestore(app)
        const projectDoc = await getDoc(doc(db, 'projects', this.projectId))
        
        if (projectDoc.exists()) {
          this.projectData = projectDoc.data()
          console.log('Project data loaded:', this.projectData)
          this.error = null
          // Reset iframe setup flag when new project data is loaded
          this.iframeSetup = false
        } else {
          console.error('Project not found with ID:', this.projectId)
          this.error = `Project not found. Please check that the project document exists in Firestore with ID: ${this.projectId}`
          // Set default data so page still displays
          this.projectData = {
            projectName: 'Project Not Found',
            dateUpdated: null
          }
        }
      } catch (error) {
        console.error('Error loading project data:', error)
        console.error('Error code:', error.code)
        console.error('Error message:', error.message)
        
        // Handle specific error cases
        if (error.code === 'permission-denied') {
          this.error = 'Permission denied. Please check your Firestore security rules allow read access to the projects collection.'
        } else if (error.code === 'unavailable') {
          this.error = 'Firestore service unavailable. Please check your connection.'
        } else {
          this.error = `Error loading project: ${error.message || 'Unknown error'}`
        }
        
        // Set default data so page still displays
        this.projectData = {
          projectName: 'Error Loading Project',
          dateUpdated: null
        }
      } finally {
        this.loading = false
      }
    },
    calculateBottomBarHeight() {
      // Calculate the actual height of the bottom bar
      this.$nextTick(() => {
        const bottomBar = this.$refs.bottomBar
        if (bottomBar) {
          this.bottomBarHeight = bottomBar.offsetHeight
          // Set CSS variable for iframe height calculation
          document.documentElement.style.setProperty('--bottom-bar-height', `${this.bottomBarHeight}px`)
        }
      })
    },
    setupMockup() {
      if (!this.projectData?.mockupHTML) return

      // If it's a full HTML document, use iframe
      if (!this.isPlainHTML && this.$refs.mockupIframe) {
        // Prevent multiple setups
        if (this.iframeSetup) {
          return
        }
        
        const iframe = this.$refs.mockupIframe
        
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow.document
          
          // Check if content is already loaded to prevent re-execution
          if (iframeDoc.body && iframeDoc.body.hasChildNodes()) {
            // Content already loaded, skip
            this.iframeSetup = true
            return
          }
          
          // Mark as setup immediately to prevent re-execution
          this.iframeSetup = true
          
          // Prepare HTML with CSS injected if provided
          let htmlToWrite = this.projectData.mockupHTML
          
          // If CSS is provided, inject it into the HTML
          if (this.projectData.mockupCSS) {
            // Check if HTML already has a <head> tag
            if (htmlToWrite.toLowerCase().includes('<head')) {
              // Inject CSS into existing head
              htmlToWrite = htmlToWrite.replace(
                /(<head[^>]*>)/i,
                `$1<style>${this.projectData.mockupCSS}</style>`
              )
            } else if (htmlToWrite.toLowerCase().includes('<html')) {
              // Has html tag but no head, add head with CSS
              htmlToWrite = htmlToWrite.replace(
                /(<html[^>]*>)/i,
                `$1<head><style>${this.projectData.mockupCSS}</style></head>`
              )
            } else {
              // No html/head structure, wrap in basic structure with CSS
              htmlToWrite = `<!doctype html><html><head><style>${this.projectData.mockupCSS}</style></head><body>${htmlToWrite}</body></html>`
            }
          }
          
          // Write the complete HTML document to the iframe
          // This will execute all scripts in the HTML including Babel transformations
          iframeDoc.open()
          iframeDoc.write(htmlToWrite)
          iframeDoc.close()
          
          // Inject additional JS after iframe loads (if provided)
          // Use a one-time event listener to avoid multiple executions
          const handleLoad = () => {
            if (this.projectData?.mockupJS) {
              try {
                const iframeDoc = iframe.contentDocument || iframe.contentWindow.document
                if (iframeDoc.body) {
                  const script = iframeDoc.createElement('script')
                  script.textContent = this.projectData.mockupJS
                  iframeDoc.body.appendChild(script)
                }
              } catch (e) {
                console.error('Error injecting JS into iframe:', e)
              }
            }
            // Remove the event listener after use
            iframe.removeEventListener('load', handleLoad)
          }
          
          iframe.addEventListener('load', handleLoad, { once: true })
          
        } catch (e) {
          console.error('Error setting up iframe content:', e)
          // Reset flag on error so we can retry
          this.iframeSetup = false
        }
      } else {
        // For plain HTML snippets, inject JavaScript normally
        this.injectJavaScript()
      }
    },
    injectJavaScript() {
      // Remove any previously injected scripts
      const existingScript = document.getElementById('mockup-injected-js')
      if (existingScript) {
        existingScript.remove()
      }

      // Inject new JavaScript if provided (only for plain HTML snippets)
      if (this.projectData?.mockupJS && this.isPlainHTML) {
        const script = document.createElement('script')
        script.id = 'mockup-injected-js'
        script.textContent = this.projectData.mockupJS
        document.body.appendChild(script)
      }
    },
    shareLink() {
      const code = sessionStorage.getItem('accessCode')
      if (!code) return
      const url = `${window.location.origin}/?code=${encodeURIComponent(code)}`
      navigator.clipboard.writeText(url).then(() => {
        this.copied = true
        setTimeout(() => { this.copied = false }, 2000)
      }).catch(() => {
        // Fallback for environments without clipboard API
        const input = document.createElement('input')
        input.value = url
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
        this.copied = true
        setTimeout(() => { this.copied = false }, 2000)
      })
    },
    goToLogin() {
      sessionStorage.removeItem('accessCode')
      sessionStorage.removeItem('accessGranted')
      this.$router.push('/')
    }
  }
}
</script>

<style scoped>
/* Preview content container - allows full customization */
.mockup-content {
  width: 100%;
  min-height: 100%;
}

/* Iframe for full HTML document rendering */
.mockup-iframe {
  width: 100%;
  height: 100%;
  min-height: calc(100vh - var(--bottom-bar-height, 80px));
  border: none;
  display: block;
}

/* Ensure bottom bar stays fixed at bottom */
.fixed.bottom-0 {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 50;
}
</style>

